import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core'
import {
  ClaimConfigurationItemODataService,
  ClaimConfigurationODataService,
  IEntityGroup,
  IClaimConfigurationItem,
  ClaimTransactionTypeODataService,
  ClaimConfigurationItemTransformationTypeODataService,
  IClaimConfiguration
} from '@exchange/domain/.'
import {
  ActionButtonsColumnRendererComponent,
  AppSelectComponent,
  ConfirmModalComponent,
  GridSize,
  GridWrapperComponent,
  Option,
  SingleRowSelection
} from 'common-components'
import { ActivatedRoute } from '@angular/router'
import { forkJoin } from 'rxjs'
import _ from 'lodash'
import { X12Codes, IX12Helper } from '@exchange/domain/lookups'
import { EntityGroupODataService } from '@exchange/domain/system/entityGroup'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'app-entity-group-edit-configs',
  templateUrl: './entity-group-edit-configs.component.html',
  standalone: false
})
export class EntityGroupEditConfigsComponent implements OnInit {
  @ViewChild('grid', { static: true }) grid: GridWrapperComponent
  @ViewChild('transactionTypeList', { static: true }) transactionTypeList: AppSelectComponent

  entityGroupId: number = null

  transactionTypeOptions: Option[] = []
  selectedTransactionType: Option = null

  transformationTypeOptions: Option[] = []

  selected: IClaimConfigurationItem = null
  claimConfigurationId: number = null

  gridSize: GridSize = GridSize.normal

  constructor(
    private route: ActivatedRoute,
    private configService: ClaimConfigurationODataService,
    private configItemService: ClaimConfigurationItemODataService,
    private transactionTypesService: ClaimTransactionTypeODataService,
    private transformationTypesService: ClaimConfigurationItemTransformationTypeODataService,
    private modalService: BsModalService
  ) {}

  setClaimConfigId(id) {
    this.claimConfigurationId = id
    this.grid.defaultFilter = `ClaimConfigurationId eq ${id}`
    this.grid.getData()
  }

  changeSelectedTransactionType = ($event: any) => {
    this.selectedTransactionType = $event
    this.configService
      .query()
      .filter(
        `EntityGroupId eq ${this.entityGroupId} and TransactionTypeId eq ${this.selectedTransactionType.id}`
      )
      .exec()
      .subscribe(result => {
        if (result.data.length) {
          this.setClaimConfigId(result.data[0].Id)
        } else {
          const model = {
            EntityGroupId: this.entityGroupId,
            TransactionTypeId: this.selectedTransactionType.id
          } as IClaimConfiguration

          this.configService
            .post(model)
            .exec()
            .subscribe(r2 => {
              this.setClaimConfigId(r2.Id)
            })
        }
      })
  }

  initializeTransformationTypes() {
    const observer = this.transformationTypesService.query().exec()

    observer.subscribe(result => {
      this.transformationTypeOptions = result.data.map(x => new Option(x.Id, x.Name, x))
    })

    return observer
  }

  initializeTransactionTypes() {
    const observer = this.transactionTypesService.query().exec()

    observer.subscribe(result => {
      this.transactionTypeOptions = result.data.map(x => new Option(x.Id, x.Name, x))

      this.changeSelectedTransactionType(this.transactionTypeOptions[0])
      this.transactionTypeList.active = [this.selectedTransactionType]
    })

    return observer
  }

  onAddEditSave = (newSelected: IClaimConfigurationItem) => {
    this.selected = newSelected
    this.selected.ClaimConfigurationId = this.claimConfigurationId

    const fn = this.selected.Id
      ? this.configItemService.put(this.selected, this.selected.Id).exec()
      : this.configItemService.post(this.selected).exec()

    fn.subscribe(() => {
      this.selected = null
      this.grid.getData()
    })
  }

  addEditClicked(data = {}) {
    this.selected = _.cloneDeep(data) as IClaimConfigurationItem
    this.modalService.show(EntityGroupEditModalComponent, {
      initialState: {
        selected: this.selected,
        transformationTypeOptions: this.transformationTypeOptions,
        onSave: this.onAddEditSave
      }
    })
  }

  deleteClicked(data) {
    this.selected = data as IClaimConfigurationItem
    this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Confirm Delete?`,
          message: `Are you sure you want to delete this item?`
        }
      })
      .content.onClose.subscribe(userConfirm => userConfirm && this.confirmDelete())
  }

  confirmDelete() {
    this.configItemService
      .delete(this.selected.Id)
      .exec()
      .subscribe(() => {
        this.selected = null
        this.grid.getData()
      })
  }

  initializeGrid() {
    this.grid.defaultExpand = 'TransformationType'
    this.grid.defaultFilter = 'ClaimConfigurationId eq -1'
    this.grid.gridOptions.context = {
      canEdit: true,
      canDelete: true,
      editHandler: this.addEditClicked.bind(this),
      deleteHandler: this.deleteClicked.bind(this)
    }
    this.grid.gridOptions.columnDefs = [
      {
        headerName: '',
        field: 'Actions',
        pinned: 'left',
        width: 200,
        cellRenderer: ActionButtonsColumnRendererComponent
      },
      {
        headerName: 'Parent Id',
        field: 'ParentSegmentId',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Parent Qualifier',
        field: 'ParentQualifierId',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Segment Id',
        field: 'SegmentId',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Element',
        field: 'ElementId',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Qualifier',
        field: 'Qualifier',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Value',
        field: 'Value',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Transformation',
        field: 'TransformationType.Name',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.grid.odata = this.configItemService
  }

  ngOnInit() {
    this.entityGroupId = +this.route.snapshot.params['id']

    this.initializeGrid()
    this.initializeTransactionTypes()
    this.initializeTransformationTypes()
  }

  doCopyFrom = (copyFromSelection: IEntityGroup, callback: () => void) => {
    this.configService
      .query()
      .filter(
        `EntityGroupId eq ${copyFromSelection.Id} and TransactionTypeId eq ${this.selectedTransactionType.id}`
      )
      .exec()
      .subscribe(source => {
        if (source.data.length > 0) {
          const sourceData = source.data[0]

          this.configItemService
            .query()
            .filter(`ClaimConfigurationId eq ${sourceData.Id}`)
            .exec()
            .subscribe(itemsSource => {
              // TODO: Implement ODATA Batch
              const observables = itemsSource.data.map(item => {
                const model = {
                  ClaimConfigurationId: this.claimConfigurationId,
                  ParentSegmentId: item.ParentSegmentId,
                  ParentQualifierId: item.ParentQualifierId,
                  SegmentId: item.SegmentId,
                  Qualifier: item.Qualifier,
                  ElementId: item.ElementId,
                  TransformationTypeId: item.TransformationTypeId,
                  Value: item.Value
                } as IClaimConfigurationItem

                return this.configItemService.post(model).exec()
              })

              forkJoin(observables).subscribe(results => {
                this.grid.getData()
                callback()
              })
            })
        } else {
          alert(`Entity group has no ${this.selectedTransactionType.text} configurations.`)
        }
      })
  }

  importClicked() {
    this.modalService.show(EntityGroupEditCopyModalComponent, {
      initialState: {
        doCopyFrom: this.doCopyFrom,
        selectedTransactionType: this.selectedTransactionType
      },
      class: 'modal-lg'
    })
  }
}

@Component({
  templateUrl: './entity-group-edit-modal.component.html',
  styleUrls: ['./entity-group-edit-modal.component.scss'],
  standalone: false
})
export class EntityGroupEditModalComponent implements AfterViewInit {
  @ViewChild('parentSegmentList') parentSegmentList: AppSelectComponent
  @ViewChild('targetSegmentList') targetSegmentList: AppSelectComponent
  @ViewChild('transformationTypeList') transformationTypeList: AppSelectComponent

  selected: IClaimConfigurationItem = null
  transformationTypeOptions: Option[] = []
  onSave: (newSelected: IClaimConfigurationItem) => void

  selectedTransformationType: Option = null

  segmentOptions: string[] = []
  FriendlyNames = X12Codes
  parentSegmentDescription(): IX12Helper {
    return X12Codes.Find('837', this.selected.ParentSegmentId, 1, this.selected.ParentQualifierId)
  }
  targetSegmentDescription(): IX12Helper {
    return X12Codes.Find(
      '837',
      this.selected.SegmentId,
      this.selected.ElementId,
      this.selected.Qualifier
    )
  }

  constructor(public modalRef: BsModalRef) {
    this.segmentOptions = this.FriendlyNames.SegmentNames().sort()
  }

  ngAfterViewInit(): void {
    this.transformationTypeList.active = this.selected?.TransformationTypeId
      ? [this.selected.TransformationTypeId]
      : []
    this.parentSegmentList.active = this.selected?.ParentSegmentId
      ? [this.selected.ParentSegmentId]
      : []
    this.targetSegmentList.active = this.selected?.SegmentId ? [this.selected.SegmentId] : []

    this.selectedTransformationType = this.transformationTypeOptions.find(
      option => option.id === this.selected?.TransformationTypeId
    )
  }

  canSave(): boolean {
    if (this.selectedTransformationType?.text === 'DeleteSegment') {
      return !!this.selected.SegmentId
    } else {
      return !!(this.selected.SegmentId && this.selected.ElementId)
    }
  }

  saveClicked() {
    this.selected.TransformationTypeId = this.selectedTransformationType.id as number
    this.onSave(this.selected)
    this.modalRef.hide()
  }

  parentSegmentSelected = (event: any) => {
    this.selected.ParentSegmentId = event?.text
  }

  parentSegmentRemoved = () => {
    this.selected.ParentSegmentId = ''
  }

  targetSegmentSelected = (event: any) => {
    this.selected.SegmentId = event?.text
  }

  targetSegmentRemoved = () => {
    this.selected.SegmentId = ''
  }

  changeSelectedTransformationType = ($event: any) => {
    this.selectedTransformationType = $event
  }
}

@Component({
  templateUrl: './entity-group-edit-copy-modal.component.html',
  standalone: false
})
export class EntityGroupEditCopyModalComponent implements OnInit {
  @ViewChild('copyFromGrid', { static: true }) copyFromGrid: GridWrapperComponent
  GridSize = GridSize
  copyFromSelection: IEntityGroup = null
  copyLoading = false

  doCopyFrom: (copyFromSelection: IEntityGroup, callback: () => void) => void
  selectedTransactionType: Option = null

  constructor(public modalRef: BsModalRef, private entityGroupService: EntityGroupODataService) {}

  ngOnInit(): void {
    this.initializeCopyFromGrid()
  }

  initializeCopyFromGrid() {
    this.copyFromGrid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }
    this.copyFromGrid.gridOptions.columnDefs = [
      {
        headerName: 'Name',
        field: 'Name',
        width: 300,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.copyFromGrid.gridOptions.rowSelection = SingleRowSelection
    this.copyFromGrid.gridOptions.onSelectionChanged = () => {
      this.copyFromSelection = this.copyFromGrid.api.getSelectedRows()[0]
    }
    this.copyFromGrid.odata = this.entityGroupService
  }

  copy = () => {
    this.copyLoading = true
    this.doCopyFrom(this.copyFromSelection, () => {
      this.copyLoading = false
      this.modalRef.hide()
    })
  }
}
